import { useEffect, useRef, useState } from "react";
import { MapPin } from "@/components/icons";

/**
 * Reusable address autocomplete powered by Mapbox Geocoding API.
 *
 * Props
 *  - value: string (the current address text)
 *  - onChange: (text: string) => void
 *  - placeholder: string
 *  - inputClassName: extra classes for the <input>
 *  - showIcon: boolean (default true) — render a MapPin icon inside the input
 *  - testid: data-testid for the input
 *  - required: boolean
 *
 * Behaviour
 *  - Debounces typing 300ms then queries Mapbox `mapbox.places`
 *  - Displays up to 5 suggestions in a dropdown anchored to the input
 *  - Clicking a suggestion (or pressing Enter on the highlighted one) fills
 *    the input with its `place_name` and closes the dropdown
 *  - Falls back to a plain input when REACT_APP_MAPBOX_TOKEN is missing
 */
export default function AddressAutocomplete({
  value,
  onChange,
  placeholder = "Dirección",
  inputClassName = "w-full bg-white border border-[#E5E7EB] rounded-2xl pl-11 pr-4 py-3 text-black placeholder-black/40 focus:outline-none focus:border-black/60",
  showIcon = true,
  testid,
  required,
}) {
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  // Track whether the latest change came from a user keystroke (which should
  // trigger a new fetch) vs. a programmatic fill from selecting a suggestion
  // (which must NOT re-open the dropdown).
  const isUserTypingRef = useRef(false);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (!isUserTypingRef.current) return;
    if (!token) return;
    const q = (value || "").trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      // Below threshold → clear and bail. Done inside the async callback so
      // we don't trigger setState synchronously from the effect setup.
      if (q.length < 3) {
        setSuggestions([]);
        setOpen(false);
        return;
      }
      try {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        setLoading(true);
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          q
        )}.json?access_token=${token}&autocomplete=true&limit=5&language=es&types=address,place,locality,neighborhood,postcode,district,region&country=do`;
        const res = await fetch(url, { signal: abortRef.current.signal });
        const data = await res.json();
        const feats = Array.isArray(data?.features) ? data.features : [];
        setSuggestions(feats);
        setHighlight(-1);
        setOpen(feats.length > 0);
      } catch (err) {
        if (err.name !== "AbortError") {
          // eslint-disable-next-line no-console
          console.warn("Mapbox geocoding failed", err);
        }
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, token]);

  const pick = (feat) => {
    isUserTypingRef.current = false;
    onChange(feat.place_name || feat.text || "");
    setOpen(false);
    setSuggestions([]);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (highlight >= 0 && highlight < suggestions.length) {
        e.preventDefault();
        pick(suggestions[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      {showIcon && (
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 pointer-events-none z-[1]" />
      )}
      <input
        type="text"
        value={value || ""}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className={inputClassName}
        autoComplete="off"
        onChange={(e) => {
          isUserTypingRef.current = true;
          onChange(e.target.value);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {open && suggestions.length > 0 && (
        <ul
          data-testid="address-suggestions"
          className="absolute z-30 left-0 right-0 mt-1.5 bg-white border border-black/10 rounded-2xl shadow-xl overflow-hidden max-h-72 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.id || `${s.place_name}-${i}`}
              onMouseDown={(e) => {
                // Use mousedown so it fires before the input's onBlur
                e.preventDefault();
                pick(s);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`flex items-start gap-2.5 px-4 py-2.5 cursor-pointer transition ${
                i === highlight ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]"
              }`}
              data-testid={`address-suggestion-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 text-[#01BC8E] mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-black truncate">
                  {s.text}
                </div>
                <div className="text-xs text-black/55 truncate">
                  {s.place_name}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {loading && open && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-black/40">
          ...
        </div>
      )}
    </div>
  );
}
